import { useState } from "react";
import { Box, Container, Typography, Link } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import ContactModal from "../landing/ContactModal";

const GOLD      = "#D97706";

const Section = ({ title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", mb: 2, fontFamily: "IBM Plex Sans, sans-serif", letterSpacing: "-.3px" }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const P = ({ children }) => (
  <Typography sx={{ fontSize: 15, color: "#475569", lineHeight: 1.85, mb: 2, fontFamily: "IBM Plex Sans, sans-serif" }}>
    {children}
  </Typography>
);

const Li = ({ children }) => (
  <Box sx={{ display: "flex", gap: 1.5, mb: 1.25 }}>
    <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: GOLD, mt: "10px", flexShrink: 0 }} />
    <Typography sx={{ fontSize: 15, color: "#475569", lineHeight: 1.8, fontFamily: "IBM Plex Sans, sans-serif" }}>{children}</Typography>
  </Box>
);

export default function TermsOfService() {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <Box sx={{ minHeight: "100vh", background: "#fff", fontFamily: "IBM Plex Sans, sans-serif" }}>
      <ContactModal open={modalOpen} onClose={() => setModalOpen(false)} />

      {/* Header */}
      <Box sx={{ background: "#0F172A", py: 5, borderBottom: "1px solid rgba(255,255,255,.06)" }}>
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
            <IconButton onClick={() => navigate("/")} sx={{ color: "#64748B", "&:hover": { color: "#fff" }, p: .5 }}>
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Box component="img" src="/logo.png" alt="AMLDiligence" sx={{ height: 34, display: "block", filter: "brightness(0) invert(1)" }} />
          </Box>
          <Typography sx={{ fontSize: { xs: 28, md: 38 }, fontWeight: 700, color: "#F1F5F9", letterSpacing: "-1px", fontFamily: "IBM Plex Sans, sans-serif", mb: 1 }}>
            Terms of Service
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#64748B", fontFamily: "IBM Plex Sans, sans-serif" }}>
            Last updated: June 2025 · Effective date: June 1, 2025
          </Typography>
        </Container>
      </Box>

      {/* Body */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ maxWidth: 760 }}>

          <P>
            These Terms of Service ("Terms") govern your access to and use of the AMLDiligence compliance platform ("Platform") and related services provided by ZAHADS Chartered Accountants LLC ("ZAHADS", "we", "us", or "our"), a company registered in the United Arab Emirates. By creating an account, accessing the Platform, or using any of our services, you ("Client", "you") agree to be bound by these Terms.
          </P>
          <P>
            If you are entering into these Terms on behalf of a company or organisation, you represent that you have the authority to bind that entity, and references to "you" include that entity.
          </P>

          <Section title="1. Description of Services">
            <P>
              AMLDiligence provides a Software-as-a-Service (SaaS) platform designed to assist Designated Non-Financial Businesses and Professions (DNFBPs) and other regulated entities with AML/CFT compliance obligations. The Platform includes, but is not limited to:
            </P>
            <Li>KYC (Know Your Customer) onboarding and customer due diligence management.</Li>
            <Li>Politically Exposed Persons (PEP) and sanctions list screening against 200+ global databases.</Li>
            <Li>Risk profiling, scoring, and categorisation of customers and transactions.</Li>
            <Li>Audit trail management and regulatory report generation.</Li>
            <Li>Ongoing transaction monitoring and alert management.</Li>
          </Section>

          <Section title="2. Account Registration and Access">
            <Li>You must provide accurate, complete, and up-to-date information during registration.</Li>
            <Li>You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account.</Li>
            <Li>You must notify us immediately by <Link component="button" onClick={() => setModalOpen(true)} sx={{ color: GOLD, cursor: "pointer", fontFamily: "IBM Plex Sans, sans-serif", fontSize: "inherit", background: "none", border: "none", padding: 0, verticalAlign: "baseline" }}>contacting us</Link> if you suspect unauthorised access to your account.</Li>
            <Li>Access is granted to your organisation only. Sharing credentials with individuals outside your organisation is prohibited.</Li>
            <Li>We reserve the right to suspend or terminate accounts that violate these Terms.</Li>
          </Section>

          <Section title="3. Client Obligations and Compliance Responsibility">
            <P>
              <strong>Important:</strong> AMLDiligence is a compliance tool. The Platform does not constitute legal advice, and final compliance decisions remain solely your responsibility. Specifically:
            </P>
            <Li>You remain solely responsible for meeting all applicable AML/CFT regulatory obligations in your jurisdiction.</Li>
            <Li>Screening results, risk scores, and reports generated by the Platform are provided as an aid to your compliance programme and must be reviewed and acted upon by qualified compliance personnel.</Li>
            <Li>You must ensure that your use of the Platform complies with UAE Federal Decree-Law No. 20 of 2018 on Anti-Money Laundering, any Cabinet Decision amendments, and all other applicable laws.</Li>
            <Li>You must not input false, misleading, or fabricated customer data into the Platform.</Li>
            <Li>You must maintain appropriate internal controls and appoint a qualified Money Laundering Reporting Officer (MLRO).</Li>
          </Section>

          <Section title="4. Fees and Payment">
            <Li>Subscription fees are as agreed in your Order Form or subscription plan and are payable in advance.</Li>
            <Li>All fees are exclusive of VAT and other applicable taxes, which will be added where required.</Li>
            <Li>Overdue payments may result in suspension of access until the outstanding balance is settled.</Li>
            <Li>Fees are non-refundable except as expressly stated in your Order Form or as required by UAE consumer protection law.</Li>
            <Li>We reserve the right to adjust pricing with 30 days' written notice for renewal periods.</Li>
          </Section>

          <Section title="5. Data Ownership and Processing">
            <Li>You retain ownership of all customer data, documents, and records you input into the Platform ("Client Data").</Li>
            <Li>You grant us a limited licence to process Client Data solely to provide and improve the services.</Li>
            <Li>We act as a data processor in respect of personal data within Client Data; you are the data controller and responsible for the lawfulness of processing.</Li>
            <Li>We process Client Data in accordance with our <Link href="/privacy-policy" sx={{ color: GOLD }}>Privacy Policy</Link> and any applicable Data Processing Agreement.</Li>
          </Section>

          <Section title="6. Intellectual Property">
            <P>
              The Platform, including all software, algorithms, designs, trade marks, and documentation, is and remains the exclusive property of ZAHADS Chartered Accountants LLC or its licensors. Nothing in these Terms transfers any intellectual property rights to you. You may not copy, reverse-engineer, decompile, or create derivative works from the Platform.
            </P>
          </Section>

          <Section title="7. Confidentiality">
            <P>
              Each party agrees to keep the other's Confidential Information strictly confidential and not to disclose it to third parties without prior written consent, except as required by law or regulation. "Confidential Information" includes the Platform's pricing, algorithms, and technical architecture, as well as any Client Data you share with us.
            </P>
          </Section>

          <Section title="8. Warranties and Disclaimers">
            <P>
              We warrant that the Platform will perform materially in accordance with its documentation during the subscription term. However, to the maximum extent permitted by law:
            </P>
            <Li>The Platform is provided "as is" and we make no warranty that it is error-free or uninterrupted.</Li>
            <Li>Sanctions and PEP database coverage depends on third-party data providers and may not be exhaustive.</Li>
            <Li>We do not warrant that use of the Platform will ensure full regulatory compliance or prevent regulatory sanctions.</Li>
          </Section>

          <Section title="9. Limitation of Liability">
            <P>
              To the fullest extent permitted by applicable law, ZAHADS's aggregate liability to you under or in connection with these Terms shall not exceed the fees paid by you in the twelve (12) months immediately preceding the claim. We shall not be liable for indirect, incidental, special, consequential, or punitive damages, including loss of profits, loss of data, or regulatory penalties, even if advised of the possibility of such damages.
            </P>
          </Section>

          <Section title="10. Indemnification">
            <P>
              You agree to indemnify, defend, and hold harmless ZAHADS and its officers, directors, employees, and agents from any claims, damages, losses, and expenses (including reasonable legal fees) arising out of: (i) your violation of these Terms; (ii) your breach of applicable AML/CFT regulations; or (iii) your Client Data infringing any third-party rights.
            </P>
          </Section>

          <Section title="11. Term and Termination">
            <Li>These Terms commence on your registration date and continue for the subscription period in your Order Form, renewing automatically unless either party provides 30 days' notice.</Li>
            <Li>Either party may terminate immediately for material breach not cured within 14 days of written notice.</Li>
            <Li>Upon termination, your access will cease and you may request an export of Client Data within 30 days.</Li>
            <Li>Sections 5, 6, 7, 9, and 10 survive termination.</Li>
          </Section>

          <Section title="12. Governing Law and Disputes">
            <P>
              These Terms are governed by the laws of the United Arab Emirates and the Emirate of Dubai. Any disputes shall be subject to the exclusive jurisdiction of the Dubai Courts, except where you elect to refer a dispute to arbitration under the DIAC Arbitration Rules.
            </P>
          </Section>

          <Section title="13. Amendments">
            <P>
              We may update these Terms at any time. We will provide 14 days' notice for material changes via email to your registered address. Continued use of the Platform after the notice period constitutes acceptance of the revised Terms.
            </P>
          </Section>

          <Section title="14. Contact">
            <Box sx={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "12px", p: 3 }}>
              <Typography sx={{ fontSize: 14, color: "#475569", lineHeight: 2, fontFamily: "IBM Plex Sans, sans-serif" }}>
                <strong style={{ color: "#0F172A" }}>ZAHADS Chartered Accountants LLC (AMLDiligence)</strong><br />
                Office 751, Tamani Art Offices, Marasi Drive<br />
                Business Bay, Dubai, UAE<br />
                Email: <Link component="button" onClick={() => setModalOpen(true)} sx={{ color: GOLD, cursor: "pointer", fontFamily: "IBM Plex Sans, sans-serif", fontSize: 14, background: "none", border: "none", padding: 0, verticalAlign: "baseline" }}>Contact us</Link><br />
                Phone: <Link href="tel:+97144519930" sx={{ color: GOLD }}>+971 (4) 451 9930</Link>
              </Typography>
            </Box>
          </Section>

        </Box>
      </Container>

      {/* Footer bar */}
      <Box sx={{ background: "#0F172A", py: 3, borderTop: "1px solid rgba(255,255,255,.06)" }}>
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: 12, color: "#4B5563", fontFamily: "IBM Plex Sans, sans-serif" }}>
              © {new Date().getFullYear()} AMLDiligence.com · ZAHADS Chartered Accountants. All rights reserved.
            </Typography>
            <Box sx={{ display: "flex", gap: 3 }}>
              {[["Privacy Policy", "/privacy-policy"], ["Terms of Service", "/terms-of-service"], ["Cookie Policy", "/cookie-policy"]].map(([label, href]) => (
                <Link key={label} href={href} underline="none" sx={{ fontSize: 12, color: "#4B5563", fontFamily: "IBM Plex Sans, sans-serif", "&:hover": { color: "#9CA3AF" } }}>{label}</Link>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
